<div class="ticket-intake-visualizer unified-flow">
  <div *ngFor="let step of steps; let i = index; trackBy: trackByStepId">
    <div
      class="unified-step"
      [class.enabled]="step.enabled"
      [class.expandable]="step.expandable"
      [class.editable]="editable"
    >
      <!-- Step Header -->
      <div class="step-header" (click)="onStepClick(step)">
        <div class="step-number">{{ i + 1 }}</div>
        <div class="step-icon-container" [class]="'step-' + (i + 1)">
          <ion-icon [name]="step.icon" class="step-icon"></ion-icon>
          <div class="step-status-indicator" *ngIf="step.overridden"></div>
        </div>

        <div class="step-details">
          <h4 class="step-title">{{ step.title }}</h4>
          <p class="step-description">{{ step.description }}</p>
        </div>

        <!-- Expand/Collapse Icon -->
        <div class="step-expand-icon" *ngIf="step.expandable">
          <ion-icon
            [name]="
              step.expanded ? 'chevron-up-outline' : 'chevron-down-outline'
            "
            class="expand-icon"
          ></ion-icon>
        </div>
      </div>

      <!-- Inline Configuration Panel -->
      <div
        class="inline-config"
        *ngIf="
          step.expanded &&
          (editable || (step.id === 'intake-processing' && editable))
        "
        [class.expanded]="step.expanded"
      >
        <!-- Intake Processing Configuration -->
        <div class="config-section" *ngIf="step.id === 'intake-processing'">
          @if (isAutomatedTicketingEnabled()) {
            <ion-list class="transparent-bg no-padding">
              <ion-item>
                <ion-label slot="end" class="margin-right-10">
                  Override default
                </ion-label>
                <label class="switch" slot="end" style="margin-left: 0">
                  <input
                    type="checkbox"
                    [(ngModel)]="config.overrideIntakeSettings"
                    (ngModelChange)="onOverrideIntakeSettingsChange()"
                  />
                  <span class="slider"></span>
                </label>
              </ion-item>
            </ion-list>

            <div class="intake-mode-selector">
              <div class="mode-options">
                <div
                  *ngFor="
                    let mode of [
                      INTAKE_MODES.IMMEDIATE,
                      INTAKE_MODES.FORM,
                      INTAKE_MODES.BOT_TRIAGE
                    ]
                  "
                  class="mode-option"
                  [class.selected]="config.intakeMode === mode"
                  [class.disabled]="!config.overrideIntakeSettings"
                  [attr.aria-disabled]="!config.overrideIntakeSettings"
                  (click)="config.intakeMode = mode; onIntakeModeChange()"
                >
                  <ion-icon
                    [name]="getModeIcon(mode)"
                    [ngClass]="{
                      'gradient-text-blue-purple':
                        mode === INTAKE_MODES.BOT_TRIAGE
                    }"
                  ></ion-icon>
                  <span
                    [ngClass]="{
                      'gradient-text-blue-purple':
                        mode === INTAKE_MODES.BOT_TRIAGE
                    }"
                    >{{ INTAKE_MODE_LABELS[mode] }}</span
                  >
                </div>
              </div>
            </div>

            <!-- Bot Triage Settings -->
            <div
              class="bot-settings"
              *ngIf="config.intakeMode === INTAKE_MODES.BOT_TRIAGE"
            >
              <div class="setting-group">
                <label class="setting-label">
                  <ion-icon name="hand-left-outline"></ion-icon>
                  <span>Handoff Mode</span>
                </label>
                <ion-select
                  [(ngModel)]="config.intakeSettings.bot.handoffMode"
                  (ionChange)="onBotSettingsChange()"
                  [disabled]="!config.overrideIntakeSettings"
                  class="select-outline"
                  interface="popover"
                  [compareWith]="compareFn"
                >
                  <ion-select-option [value]="AGENT_HANDOFF_MODES.PROMPT">
                    {{ AGENT_HANDOFF_MODE_LABELS[AGENT_HANDOFF_MODES.PROMPT] }}
                  </ion-select-option>
                  <ion-select-option [value]="AGENT_HANDOFF_MODES.AUTOMATIC">
                    {{
                      AGENT_HANDOFF_MODE_LABELS[AGENT_HANDOFF_MODES.AUTOMATIC]
                    }}
                  </ion-select-option>
                </ion-select>
              </div>

              <div class="setting-group">
                <label class="setting-label">
                  <ion-icon name="repeat-outline"></ion-icon>
                  <span>Maximum Responses</span>
                </label>
                <ion-input
                  type="number"
                  min="1"
                  [(ngModel)]="botMaxResponsesInput"
                  (ionBlur)="updateMaxResponses()"
                  [disabled]="!config.overrideIntakeSettings"
                  placeholder="Unlimited"
                  class="input-field max-responses-input-field"
                ></ion-input>
              </div>
            </div>
          } @else {
            <div class="info-notice">
              <ion-icon name="information-circle-outline"></ion-icon>
              <p>
                Automatic conversation tracking is disabled. Tickets can be
                created either manually or by emoji reactions.
              </p>
            </div>
          }
        </div>

        <!-- New Message Received Configuration -->
        <div *ngIf="step.id === 'intake'">
          <div class="config-section" *ngIf="editable">
            <ion-list class="transparent-bg">
              <ion-item>
                <ion-label text-wrap
                  >Automatically track new messages as conversations</ion-label
                >
                <ion-select
                  [(ngModel)]="config.automatedTicketingEnabled"
                  (ionChange)="onAutomatedTicketingChange($event)"
                  class="select-outline"
                  interface="popover"
                  [compareWith]="compareFn"
                >
                  <ion-select-option [value]="null">
                    Default ({{ automatedTicketingDefaultLabel() }})
                  </ion-select-option>
                  <ion-select-option [value]="false"
                    >Disabled</ion-select-option
                  >
                  <ion-select-option [value]="true">Enabled</ion-select-option>
                </ion-select>
              </ion-item>
            </ion-list>
          </div>
        </div>

        <!-- Auto-Response Configuration -->
        <div *ngIf="step.id === 'auto-responder'">
          <!-- Autoresponder Component -->
          <div class="autoresponder-container" *ngIf="editable">
            <app-autoresponder
              [isDefault]="false"
              [initialAutoresponder]="config.autoresponder"
              [projectId]="projectId"
              (autoresponderDidChange)="onAutoresponderChange($event)"
            ></app-autoresponder>
          </div>
        </div>

        <!-- SLA Configuration -->
        <div *ngIf="step.id === 'sla-assignment'">
          <!-- SLA Ruleset Selector (when in channel context) -->
          <ion-list
            *ngIf="showSlaDropdown && editable"
            class="no-padding transparent-bg"
          >
            <ion-item>
              <ion-label>SLA Ruleset</ion-label>
              <ion-select
                [(ngModel)]="config.supportStepRulesetId"
                (ionChange)="onSlaRulesetChange($event)"
                interface="popover"
                placeholder="Select a ruleset"
                slot="end"
                [compareWith]="compareFn"
              >
                <ion-select-option value="default">Default</ion-select-option>
                <ion-select-option value="custom">Custom</ion-select-option>
                <ion-select-option
                  *ngFor="let ruleset of supportStepRulesets"
                  [value]="ruleset.id"
                >
                  {{ ruleset.name }}
                </ion-select-option>
              </ion-select>
            </ion-item>
          </ion-list>

          <!-- Support Steps Component -->
          <div
            class="support-steps-container"
            *ngIf="editable && config.supportSteps"
          >
            <app-support-steps
              [initialSupportSteps]="config.supportSteps"
              [disabled]="!editable || isSupportStepsDisabled()"
              [createdBy]="createdBy"
              [projectId]="projectId"
              [customerId]="customerId"
              [loading]="false"
              (didChange)="onSupportStepsChange($event)"
            ></app-support-steps>
          </div>

          <div
            style="text-align: center; margin-bottom: 10px; margin-top: 5px"
            *ngIf="config.supportStepRulesetId === 'default'"
          >
            <ion-button
              fill="outline"
              *ngIf="projectId"
              [routerLink]="
                '/dashboard/settings/projects/' + projectId + '/settings'
              "
              routerDirection="root"
            >
              <ion-icon name="create-outline" class="padded-icon"></ion-icon>
              Edit project-level defaults
            </ion-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Connection Arrow (between steps) -->
    <div class="step-connector" *ngIf="i < steps.length - 1">
      <div class="connector-line"></div>
      <ion-icon name="chevron-down" class="connector-arrow"></ion-icon>
    </div>
  </div>
</div>
